<script setup>
import {
  ref,
  onBeforeMount,
  onUnmounted,
  computed,
  watch } from "vue";
// import { ColorPicker } from "vue3-colorpicker";
// import "vue3-colorpicker/style.css";

import { useRoute } from "vue-router";
import router from "@/router";
import generateFromLogo from "./generateFromLogo.vue";
import AgentLayout from "@/views/layouts/AgentLayout.vue";
import DBreadcrumb from "@/components/shared/DBreadcrumb.vue";
import { useCompanyStore } from "@/stores/company";
import { useUiStore } from "@/stores/ui";
import { useThemesStore } from "@/stores/themes";
import chroma from "chroma-js";

import axios from "axios";
import { useToast } from "vue-toastification";
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";

const company_id = useCompanyStore().companyId;
const toast = useToast();

const uiStore = useUiStore();
const themesStore = useThemesStore();
const route = useRoute();
let id = ref(null);
let name = ref(null);
let description = ref(null);
let properties = ref({
  assets: {},
  layout: {},
  palette: {
    header: {},
    chat: {},
    agent: {},
    product: {},
    menu: {},
    brand_primary: {},
    main_background_color: {},
    card_background: {},
    card_border_color: {},
    text_primary: {},
    text_secondary: {},
  },
  typography: {},
});

let isSubmitted = ref(false);
let isLoading = ref(true);
let isStoreLoading = ref(false);
let isCartOpen = ref(false);
let isDefault = ref(false);
let type = ref(null);
let logoFiles = ref([]);
let activePaletteIndex = ref(0);

let headers = {
  Accept: "application/json",
  "Content-type": "multipart/form-data",
};
let uploadPercentage = ref(0);

watch(
  () => properties.value?.palette?.menu,
  () => {
    if (!isLoading.value) isCartOpen.value = true;
  },
  { deep: true },
);

watch(
  [
    () => properties.value?.palette?.header,
    () => properties.value?.palette?.chat,
    () => properties.value?.palette?.agent,
    () => properties.value?.palette?.product,

    () => properties.value?.palette?.main_background_color,
    () => properties.value?.palette?.card_background,
    () => properties.value?.palette?.card_border_color,
    () => properties.value?.palette?.text_secondary,

    () => properties.value?.assets,
    () => properties.value?.layout,
    () => properties.value?.typography,
  ],
  () => {
    if (!isLoading.value) isCartOpen.value = false;
  },
  { deep: true },
);

const generateFromLogoVisible = ref(false);

const generateFromLogoModal = (type) => {
  generateFromLogoVisible.value = type === "show";
};

const updateThemeBuilder = async () => {
  const data = {
    id: id.value,
    company_id,
    data: {
      name: name.value,
      description: description.value,
      properties: properties.value,
    },
  };
  await themesStore
    .editTheme(data)
    .then(() => {
      router.push("/themes");
    })
    .catch(() => {
      isSubmitted.value = false;
    });
};
const uploadFiles = async () => {
  let files = logoFiles.value;
  let formData = new FormData();
  for (let i = 0; i < files.length; i++) {
    formData.append(`files[${i}][file]`, files[i].file);
    formData.append(`files[${i}][type]`, files[i].type);
  }
  await axios
    .post(
      `${process.env.VUE_APP_BASE_URL}/api/s3-upload/${company_id}`,
      formData,
      {
        headers: headers,
        withCredentials: true,
        onUploadProgress: function (progressEvent) {
          uploadPercentage.value = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100),
          );
        }.bind(this),
      },
    )
    .then(async (res) => {
      if (res.data && res.data.data && res.data.data.length) {
        await res.data.data.map((item) => {
          if (item.type == "logo") {
            properties.value.assets.logo_icon_url = item.url;
          } else if (item.type == "avatar") {
            properties.value.assets.chat_bot_url = item.url;
          }
        });
        await updateThemeBuilder();
      }
    })
    .catch((e) => {
      toast.error(e.message);
    });
};

const onSubmit = async () => {
  isSubmitted.value = true;
  if (logoFiles.value.length) await uploadFiles();
  else await updateThemeBuilder();
};

const onRestoreDefault = async () => {
  isStoreLoading.value = true;
  themesStore
    .restoreDefaultTheme({
      type: type.value,
      company_id,
    })
    .then(() => {
      //      properties.value = res.data.properties;
      isStoreLoading.value = false;
      isSubmitted.value = false;
    })
    .catch(() => {
      isSubmitted.value = false;
    });
};

const colorsGeneratedFromLogo = (payload) => {
  generateFromLogoModal("hide");
  // Destructure payload (colors, source, type)
  const { colors, source, type } = payload;

  if (!colors || colors.length === 0) return;
  logoFiles.value = [];
  // 1. Identify Primary Color
  // Check if any color was selected by the user
  const selectedColorObj = colors.find((c) => c.selected);
  let primaryColorHex = selectedColorObj ? selectedColorObj.hex : null;

  // If no color selected, pick the most dominant one (highest percentage)
  if (!primaryColorHex) {
    primaryColorHex = colors[0].hex;
  }

  // const primaryChroma = chroma(primaryColorHex);
  const otherColors = colors.filter((c) => c.hex !== primaryColorHex);

  // --- Helpers ---
  // User Request: set text primary and secondary based on brand primary darkness

  // Check luminance of primary color to determine text color
  const isPrimaryDark = chroma(primaryColorHex).luminance() < 0.5;

  // If primary is dark, use light text (#E0E0E0). If primary is light, use dark text (#333333).
  // Note: Both text_primary and text_secondary are set to the same color as requested.
  let textPrimary = isPrimaryDark ? "#FFFFFF" : "#333333";
  let textSecondary = isPrimaryDark ? "#FFFFFF" : "#333333";

  const white = "#ffffff";

  properties.value.assets.background_url = null;

  // --- Apply to Palette ---

  // Set Main Palette
  properties.value.palette.brand_primary = primaryColorHex;
  properties.value.palette.main_background_color = "#F9F9F9";
  properties.value.palette.card_background = "#F5F5F5"; // Keep default or potentially use very light tint of primary: primaryChroma.brighten(4).hex()
  properties.value.palette.card_border_color = "#E0E0E0";
  properties.value.palette.text_primary = textPrimary;
  properties.value.palette.text_secondary = textSecondary;
  properties.value.palette.status_success = "#2E7D32"; // Don't override status colors unless requested
  properties.value.palette.status_error = "#C62828";

  // Header
  // Request implies updated related to color selected.
  // Before: header_color = primaryColorHex
  if (!properties.value.palette.header) properties.value.palette.header = {};
  properties.value.palette.header.header_color = primaryColorHex;
  properties.value.palette.header.header_logo_background = white;
  properties.value.palette.header.header_bottom_border = primaryColorHex;

  // Handle Source (Logo/URL) for Preview
  if (properties.value.assets === undefined) properties.value.assets = {};

  if (type === "url" && source) {
    properties.value.assets.logo_icon_url = source;
    properties.value.assets.chat_bot_url = source;
  } else if (type === "logo" && source) {
    // Create a blob URL for previewing the file
    try {
      const objectUrl = URL.createObjectURL(source);
      properties.value.assets.logo_icon_url = objectUrl;
      properties.value.assets.chat_bot_url = objectUrl;

      logoFiles.value.push({
        type: "logo",
        file: source,
      });
      logoFiles.value.push({
        type: "avatar",
        file: source,
      });
    } catch (e) {
      console.error("Error creating object URL for logo:", e);
    }
  }

  // Chat
  if (!properties.value.palette.chat) properties.value.palette.chat = {};

  // Try to find a very light background color from the logo (e.g. if logo has light grey or beige)
  // If not, use a very light tint of primary, or standard off-white
  let chatBg =
    otherColors.find((c) => chroma(c.hex).luminance() > 0.9)?.hex || "#f9f9f9";

  // For bot bubble, use a light tint of primary to keep it branded but distinct from user bubble
  // Or use a secondary light color from logo
  let botBubble =
    otherColors.find((c) => {
      const lum = chroma(c.hex).luminance();
      return lum > 0.8 && lum < 0.95 && c.hex !== chatBg;
    })?.hex || chroma(primaryColorHex).brighten(3).hex();

  // Ensure bot bubble is not too dark (must have dark text on it) or too light (invisible against white/chatBg)
  // If too light/white, darken it slightly unless chatBg is dark
  if (chroma(botBubble).luminance() > 0.95) {
    botBubble = chroma(botBubble).darken(0.1).hex();
  }

  properties.value.palette.chat.chat_background = "#F9F9F9";
  properties.value.palette.chat.chat_title_background = primaryColorHex;
  properties.value.palette.chat.chat_title_color =
    chroma(primaryColorHex).luminance() > 0.5 ? textPrimary : white;

  properties.value.palette.chat.chat_bubble_bot = botBubble;
  properties.value.palette.chat.text_color_bot =
    chroma(botBubble).luminance() > 0.5 ? "#333333" : "#ffffff"; // Bot text usually dark

  properties.value.palette.chat.chat_bubble_user = primaryColorHex;
  properties.value.palette.chat.text_color_user =
    chroma(primaryColorHex).luminance() > 0.5 ? "#000000" : "#ffffff";

  properties.value.palette.chat.chat_bubble_stroke_bot = botBubble; // Match bubble fill
  properties.value.palette.chat.chat_background_transparent = false;
  properties.value.palette.chat.chat_title_background_transparent = false;

  // User Request:
  // "when recieving the olors in colorsGeneratedFromLogo set secondaryColorHex as top percentage but selected is equal false and set secondaryColorHex as background for stop and refresh and record buttons and check color secondaryColorHex darl or light to set text color for btns"

  let secondaryColorHex = null;
  // 1. Find top percentage color that is NOT selected (and thus not the primary we just set, assuming user selected one).
  // The 'colors' array is already sorted by percentage descending? No, let's verify sort.
  // Assuming 'colors' comes in sorted by percentage from generateFromLogo component.
  // We need the first color where selected == false.

  const topSecondary = colors.find(
    (c) => !c.selected && c.hex !== primaryColorHex,
  );

  if (topSecondary) {
    secondaryColorHex = topSecondary.hex;
  } else {
    // Fallback if logo only has 1 color or all selected?
    // Just use a complementary or default
    secondaryColorHex = "#FFC220";
  }

  // 2. Set this as background for stop/refresh/record
  let endSessionBg = "#C32928";
  let refreshBg = secondaryColorHex;
  let recordBg = secondaryColorHex;
  let muteBg = secondaryColorHex;

  // 3. Check color secondaryColorHex dark or light to set text color
  // Since we already have textPrimary (#333 or #FFF based on primary brand), maybe we should check against standard black/white for buttons?
  // Usually buttons want high contrast.
  const getContrastText = (bg) =>
    chroma(bg).luminance() > 0.5 ? "#333333" : "#ffffff";

  // Update logic to use this new contrast text helper
  // ...

  // Agent
  if (!properties.value.palette.agent) properties.value.palette.agent = {};
  properties.value.palette.agent.start_session_background_btn = primaryColorHex;
  properties.value.palette.agent.start_session_text_btn =
    chroma(primaryColorHex).luminance() > 0.5 ? textPrimary : white;

  properties.value.palette.agent.end_session_background_btn = endSessionBg;
  properties.value.palette.agent.end_session_text_btn =
    getContrastText(endSessionBg);

  properties.value.palette.agent.chat_note_text_color = "#333333";

  properties.value.palette.agent.refresh_background_btn = refreshBg;
  properties.value.palette.agent.refresh_icon_color =
    getContrastText(refreshBg);

  properties.value.palette.agent.mute_background_btn = muteBg;
  properties.value.palette.agent.mute_icon_color = muteBg;

  properties.value.palette.agent.send_btn_color = primaryColorHex;

  properties.value.palette.agent.record_background_btn = recordBg;
  properties.value.palette.agent.record_icon_color = getContrastText(recordBg);

  // Product
  if (!properties.value.palette.product) properties.value.palette.product = {};
  properties.value.palette.product.product_title = primaryColorHex;
  properties.value.palette.product.product_description =
    chroma(primaryColorHex).luminance() > 0.5 ? "#ffffff" : "#333333";

  // Menu
  if (!properties.value.palette.menu) properties.value.palette.menu = {};
  properties.value.palette.menu.menu_background = white;
  properties.value.palette.menu.menu_title_color = primaryColorHex;
  properties.value.palette.menu.menu_plus_btn_background_color =
    primaryColorHex;
};

onBeforeMount(async () => {
  localStorage.setItem("sidebarCollapsed", true);
  id.value = route.params.id;
  themesStore
    .fetchThemeById({ company_id, id: id.value })
    .then(async (res) => {
      name.value = res.data.name;
      description.value = res.data.description;
      isDefault.value = res.data.is_default;
      type.value = res.data.type;
      if (res && res.data && res.data.properties) {
        properties.value = res.data.properties;
      }
      isLoading.value = false;
    })
    .catch(() => {});
});

const resetStoreData = () => {
  uiStore.resetModalStore();
};

onUnmounted(async () => {
  await localStorage.setItem("sidebarCollapsed", false);
  resetStoreData();
});

const breadcrumbItems = computed(() => [
  { text: "Themes", to: "/themes" },
  { text: name.value || "Loading...", active: true },
]);

const activeTab = ref("general"); // general | layout | palette | typography
const tabs = [
  { id: "general", label: "Assets", icon: "bi bi-images" },
  { id: "layout", label: "Layout", icon: "bi bi-grid-1x2" },
  { id: "palette", label: "Palette", icon: "bi bi-palette" },
  { id: "typography", label: "Typography", icon: "bi bi-fonts" },
  { id: "generateFromLogo", label: "Generate From Logo", icon: "bi bi-magic" },
];

const setActiveTab = (tabId) => {
  if (tabId === "generateFromLogo") {
    generateFromLogoModal("show");
  } else {
    activeTab.value = tabId;
  }
};

/* Font Handling */
const currentFontFamily = ref('"Inter", sans-serif');

const loadFont = (url) => {
  if (!url || !url.trim()) {
    currentFontFamily.value = '"Inter", sans-serif';
    return;
  }

  try {
    // If it looks like a URL, try to load it
    if (url.includes("http") || url.includes("fonts.googleapis.com")) {
      let link = document.getElementById("theme-custom-font");
      if (!link) {
        link = document.createElement("link");
        link.id = "theme-custom-font";
        link.rel = "stylesheet";
        document.head.appendChild(link);
      }
      link.href = url;

      // Try to extract family name from Google Fonts URL
      const urlObj = new URL(url);
      const familyParam = urlObj.searchParams.get("family");
      if (familyParam) {
        // e.g. "Roboto:wght@400" -> "Roboto"
        // e.g. "Open+Sans" -> "Open Sans"
        let family = familyParam.split(":")[0];
        family = family.replace(/\+/g, " ");
        currentFontFamily.value = `'${family}', sans-serif`;
      }
    } else {
      // Assume it's a system font or manually entered name
      currentFontFamily.value = `'${url}', sans-serif`;
    }
  } catch (e) {
    // Fallback if URL parsing fails but might be a name
    currentFontFamily.value = `'${url}', sans-serif`;
  }
};

watch(
  () => properties.value?.typography?.font_family_url,
  (newVal) => {
    loadFont(newVal);
  },
  { immediate: true },
);

const previewStyle = computed(() => {
  const p = properties.value;
  if (!p || !p.palette) return {};

  return {
    "--main-bg": p.assets?.background_url
      ? `url(${p.assets.background_url})`
      : p.palette.main_background_color || "#ffffff",
    "--font-family": currentFontFamily.value,
    "--brand-primary": p.palette.brand_primary || "#DF4918",
    "--card-bg": p.palette.card_background || "#ffffff",
    "--card-border": p.palette.card_border_color || "#e2e8f0",
    "--text-primary": p.palette.text_primary || "#334155",
    "--text-secondary": p.palette.text_secondary || "#64748b",

    // Header
    "--header-bg": p.palette.header?.header_color || "#ffffff",
    "--header-logo-bg": p.palette.header?.header_logo_background || "#ffffff",
    "--header-border-bottom-left-radius":
      `${p.layout?.header_bottom_radius}px` || "transparent",
    "--header-border-bottom-right-radius":
      `${p.layout?.header_bottom_radius}px` || "transparent",

    // Chat
    "--chat-bg": p.palette.chat?.chat_background || "transparent",
    "--chat-title-bg": p.palette.chat?.chat_title_background || "transparent",
    "--bot-bubble": p.palette.chat?.chat_bubble_bot || "#f1f5f9",
    "--user-bubble": p.palette.chat?.chat_bubble_user || "#DF4918",
    "--bot-text": p.palette.chat?.text_color_bot || "#334155",
    "--user-text": p.palette.chat?.text_color_user || "#ffffff",
    "--chat-title-color": p.palette.chat?.chat_title_color || "#64748b",

    // Agent
    "--btn-start-bg":
      p.palette.agent?.start_session_background_btn || "#ffffff",
    "--btn-start-text": p.palette.agent?.start_session_text_btn || "#000000",
    "--btn-end-bg": p.palette.agent?.end_session_background_btn || "#dc3545",
    "--btn-end-text": p.palette.agent?.end_session_text_btn || "#ffffff",
    "--chat-note-text-color":
      p.palette.agent?.chat_note_text_color || "#64748b",
    "--btn-refresh-bg": p.palette.agent?.refresh_background_btn || "#ffffff",
    "--btn-refresh-icon-color":
      p.palette.agent?.refresh_icon_color || "#ffffff",
    "--btn-mute-bg": p.palette.agent?.mute_background_btn || "#ffffff",
    "--btn-mute-icon-color": p.palette.agent?.mute_icon_color || "#ffffff",
    "--btn-send-bg": p.palette.agent?.send_btn_color || "#1E293B",
    "--btn-record-bg": p.palette.agent?.record_background_btn || "#EF1106",
    "--btn-record-icon-color": p.palette.agent?.record_icon_color || "#ffffff",

    // Product
    "--product-title": p.palette.product?.product_title || "#334155",
    "--product-desc": p.palette.product?.product_description || "#64748b",

    // Layout
    "--radius": p.layout?.border_radius
      ? `${p.layout.border_radius}px`
      : "12px",
    "--card-padding": p.layout?.card_padding
      ? `${p.layout.card_padding}px`
      : "16px",

    // Menu
    "--menu-background": p.palette.menu?.menu_background || "#ffffff",
    "--menu-title-color": p.palette.menu?.menu_title_color || "#334155",
    "--menu-plus-btn-background-color":
      p.palette.menu?.menu_plus_btn_background_color || "#ffffff",
    "--menu-plus-btn-icon-color":
      p.palette.menu?.menu_plus_btn_icon_color || "#ffffff",
  };
});
</script>

<template>
  <AgentLayout>
    <generateFromLogo
      :visible="generateFromLogoVisible"
      @update:visible="generateFromLogoVisible = $event"
      @generateFromLogo="colorsGeneratedFromLogo"
    />

    <div v-if="isLoading" class="theme-builder-skeleton" aria-busy="true">
      <!-- Top Header — mirrors .editor-header -->
      <header class="tb-skel-header">
        <div class="tb-skel-header__left">
          <div class="tb-skel-line tb-skel-line--breadcrumb"></div>
        </div>
        <div class="tb-skel-header__center">
          <div class="tb-skel-line tb-skel-line--label-xs"></div>
          <div class="tb-skel-line tb-skel-line--label-md"></div>
        </div>
        <div class="tb-skel-header__right">
          <div class="tb-skel-button tb-skel-button--ghost"></div>
          <div class="tb-skel-button tb-skel-button--primary"></div>
        </div>
      </header>

      <div class="tb-skel-container">
        <!-- Left Sidebar — mirrors .editor-sidebar -->
        <aside class="tb-skel-sidebar">
          <div class="tb-skel-sidebar__title">
            <div class="tb-skel-line tb-skel-line--section"></div>
          </div>
          <nav class="tb-skel-nav">
            <div
              v-for="i in 5"
              :key="`tb-skel-nav-${i}`"
              class="tb-skel-nav-item"
              :class="{ 'tb-skel-nav-item--active': i === 1 }"
            >
              <div class="tb-skel-circle tb-skel-circle--icon"></div>
              <div class="tb-skel-line tb-skel-line--nav"></div>
            </div>
          </nav>
        </aside>

        <!-- Preview Area — mirrors .editor-preview -->
        <section class="tb-skel-preview">
          <div class="tb-skel-preview__header">
            <div class="tb-skel-line tb-skel-line--preview-label"></div>
          </div>
          <div class="tb-skel-preview__stage">
            <div class="tb-skel-device">
              <!-- Header container (brand area) -->
              <div class="tb-skel-device__header">
                <div class="tb-skel-device__statusbar">
                  <div class="tb-skel-circle tb-skel-circle--logo"></div>
                  <div class="tb-skel-device__status-right">
                    <div class="tb-skel-circle tb-skel-circle--xxs"></div>
                    <div class="tb-skel-line tb-skel-line--clock"></div>
                  </div>
                </div>
              </div>

              <!-- Sub-header (tabs + cart) -->
              <div class="tb-skel-device__subheader">
                <div class="tb-skel-device__tabs">
                  <div class="tb-skel-pill tb-skel-pill--active"></div>
                  <div class="tb-skel-pill"></div>
                </div>
                <div class="tb-skel-circle tb-skel-circle--xs"></div>
              </div>

              <!-- Product card -->
              <div class="tb-skel-device__content">
                <div class="tb-skel-product">
                  <div class="tb-skel-block tb-skel-block--image"></div>
                  <div class="tb-skel-line tb-skel-line--product-title"></div>
                  <div class="tb-skel-line tb-skel-line--product-desc"></div>
                  <div class="tb-skel-pill tb-skel-pill--price"></div>
                </div>
              </div>

              <!-- Chat overlay -->
              <div class="tb-skel-device__chat">
                <div class="tb-skel-chat__header">
                  <div class="tb-skel-line tb-skel-line--chat-title"></div>
                  <div class="tb-skel-line tb-skel-line--chat-status"></div>
                </div>
                <div class="tb-skel-chat__messages">
                  <div class="tb-skel-msg tb-skel-msg--user">
                    <div class="tb-skel-bubble tb-skel-bubble--user"></div>
                    <div class="tb-skel-circle tb-skel-circle--avatar"></div>
                  </div>
                  <div class="tb-skel-msg tb-skel-msg--bot">
                    <div class="tb-skel-circle tb-skel-circle--avatar"></div>
                    <div class="tb-skel-bubble tb-skel-bubble--bot"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Settings Panel — mirrors .editor-settings -->
        <section class="tb-skel-settings">
          <div class="tb-skel-settings__title">
            <div class="tb-skel-line tb-skel-line--heading"></div>
          </div>
          <div class="tb-skel-settings__content">
            <div
              v-for="i in 6"
              :key="`tb-skel-input-${i}`"
              class="tb-skel-input-field"
            >
              <div class="tb-skel-input-field__label"></div>
              <div class="tb-skel-input-field__input"></div>
            </div>
          </div>
        </section>
      </div>
    </div>

    <div v-else class="theme-editor-wrapper">
      <!-- Top Bar -->
      <header class="editor-header">
        <div class="header-left">
          <DBreadcrumb :items="breadcrumbItems" class="mb-0" />
        </div>
        <div class="header-center">
          <span class="text-gray-500 small mr-2">Editing:</span>
          <strong>{{ name }}</strong>
        </div>
        <div class="header-right">
          <button
            v-if="isDefault"
            :disabled="isSubmitted"
            @click="onRestoreDefault"
            type="button"
            class="btn btn-primary btn-sm px-4"
          >
            <span
              v-if="isStoreLoading"
              class="spinner-sm mr-2"
              role="status"
              aria-hidden="true"
            ></span>
            Restore Default
          </button>
          <button
            :disabled="isSubmitted"
            @click="onSubmit"
            type="button"
            class="btn btn-primary btn-sm px-4"
          >
            <span
              v-if="isSubmitted"
              class="spinner-sm mr-2"
              role="status"
              aria-hidden="true"
            ></span>
            Save Changes
          </button>
        </div>
      </header>

      <div class="editor-container">
        <!-- 1. Navigation Sidebar -->
        <aside class="editor-sidebar">
          <div class="sidebar-title">Theme Editor</div>
          <nav class="nav-menu">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              class="nav-item"
              :class="{ active: activeTab === tab.id }"
              @click="setActiveTab(tab.id)"
            >
              <i :class="tab.icon"></i>
              <span>{{ tab.label }}</span>
            </button>
          </nav>
        </aside>

        <!-- 3. Live Preview -->
        <div class="editor-preview">
          <div class="preview-header">Live Preview</div>
          <div class="preview-stage">
            <div class="mobile-device" :style="previewStyle">
              <!-- Header Container -->
              <div class="header-container">
                <!-- Status Bar Row -->
                <div class="status-bar-row">
                  <div class="brand-logo">
                    <img
                      v-if="
                        properties.assets && properties.assets.logo_icon_url
                      "
                      :src="properties.assets.logo_icon_url"
                      alt="Logo"
                    />
                    <div v-else class="logo-placeholder">LOGO</div>
                  </div>
                  <div class="status-right">
                    <i class="bi bi-wifi"></i>
                    <span class="time">2026-01-13 11:15:18</span>
                  </div>
                </div>

                <!-- Tabs Row -->
                <!-- Removed from here -->
              </div>

              <!-- Sub-header Row (Grey Area) -->
              <div class="sub-header-row">
                <div class="tabs-group">
                  <div class="header-tab active">Home</div>
                  <div class="header-tab">Category</div>
                </div>
                <div class="cart-icon" @click="isCartOpen = !isCartOpen">
                  <i class="bi bi-cart3"></i>
                </div>
              </div>

              <!-- Cart Overlay -->
              <div v-if="isCartOpen" class="cart-overlay">
                <div class="cart-header">
                  <div class="cart-title">
                    <i
                      class="bi bi-cart3"
                      style="color: var(--brand-primary)"
                    ></i>
                    Your Cart
                  </div>
                  <div class="close-btn" @click="isCartOpen = false">
                    <i class="bi bi-x-lg"></i>
                  </div>
                </div>

                <div class="cart-body">
                  <div class="cart-total-section">
                    <span class="total-label">Total:</span>
                    <span class="total-price">6.73 USD</span>
                  </div>

                  <button class="checkout-btn">Proceed to Checkout</button>

                  <div class="cart-item">
                    <!-- Top Row: Image | Name | X -->
                    <div class="cart-item-top">
                      <div class="item-thumb-placeholder">
                        <i class="bi bi-image"></i>
                      </div>
                      <div class="item-name">Loop</div>
                      <button class="item-remove">
                        <i
                          class="bi bi-x"
                          style="color: var(--brand-primary)"
                        ></i>
                      </button>
                    </div>

                    <!-- Bottom Row: Price | Quantity -->
                    <div class="cart-item-bottom">
                      <span class="item-price">6.73 USD</span>
                      <div class="quantity-controls">
                        <button class="qty-btn">
                          <i class="bi bi-dash"></i>
                        </button>
                        <span class="qty-val">1</span>
                        <button class="qty-btn">
                          <i class="bi bi-plus"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Main Content -->
              <div class="app-content">
                <!-- Product Card -->
                <div class="product-card">
                  <div class="product-image-placeholder">
                    <i class="bi bi-image"></i>
                  </div>
                  <h3 class="product-title">Loop</h3>
                  <p class="product-desc">Creamy Cappuccino Mini</p>
                  <button class="price-btn">6.73 USD</button>
                </div>
              </div>

              <!-- Chat Overlay -->
              <div class="chat-overlay">
                <div class="chat-header-small">
                  <div>
                    <i
                      class="bi bi-robot"
                      style="color: var(--brand-primary)"
                    ></i>
                    Your Assistant
                  </div>
                  <span class="status-text">Active</span>
                </div>
                <div class="chat-messages">
                  <div class="msg user-msg">
                    <span>I want matte lipstick.</span>
                    <div class="avatar">
                      <img
                        class="avatar-img"
                        v-if="properties.assets.chat_user_url"
                        :src="properties.assets.chat_user_url"
                        alt=""
                      />
                      <i v-else class="bi bi-person"></i>
                    </div>
                  </div>
                  <div class="msg bot-msg">
                    <div class="avatar">
                      <img
                        class="avatar-img"
                        v-if="properties.assets.chat_bot_url"
                        :src="properties.assets.chat_bot_url"
                        alt=""
                      />
                      <i v-else class="bi bi-robot"></i>
                    </div>
                    <span
                      >I recommend the Loop Creamy Cappuccino Mini
                      lipstick...</span
                    >
                  </div>
                </div>
              </div>

              <!-- Footer Controls -->
              <!-- Volume Control Row -->

              <!-- Footer Controls -->
              <div class="app-footer">
                <div class="control-group left">
                  <!-- Volume removed from here -->
                </div>

                <div class="control-group chat-note-text">
                  <span>Tap to Stop</span>
                  <button class="record-btn">
                    <i class="bi bi-mic mic-icon"></i>
                  </button>
                </div>

                <div class="control-group">
                  <button class="end-session-btn">End Session</button>
                </div>
              </div>
              <div
                class="volume-container"
                style="
                  background: var(--chat-bg);
                  padding: 0 15px;
                  display: flex;
                  align-items: center;
                "
              >
                <div class="flex items-center gap-2 volume-up-section">
                  <i
                    class="bi bi-volume-up volume-up-icon"
                    :style="{
                      color: properties.palette.agent.mute_icon_color,
                    }"
                  ></i>
                  <div
                    class="flex items-center"
                    style="width: 100px; position: relative"
                  >
                    <div
                      style="height: 3px; width: 100%"
                      :style="{
                        backgroundColor:
                          properties.palette.agent.mute_icon_color,
                      }"
                    ></div>
                    <div
                      style="
                        width: 8px;
                        height: 8px;
                        border-radius: 50%;
                        position: absolute;
                        right: 0;
                      "
                      :style="{
                        backgroundColor:
                          properties.palette.agent.mute_icon_color,
                      }"
                    ></div>
                  </div>
                </div>
              </div>

              <!-- <div class="app-footer">
                <div class="control-group left">
                  <div class="flex items-center gap-2">
                    <i
                      class="bi bi-volume-up"
                      style="font-size: 1.2rem"
                      :style="{
                        color: properties.palette.agent.mute_icon_color,
                      }"
                    ></i>
                    <div
                      class="flex items-center"
                      style="width: 100px; position: relative"
                    >
                      <div
                        style="height: 2px; width: 100%"
                        :style="{
                          backgroundColor:
                            properties.palette.agent.mute_icon_color,
                        }"
                      ></div>
                      <div
                        style="
                          width: 8px;
                          height: 8px;
                          border-radius: 50%;
                          position: absolute;
                          right: 0;
                        "
                        :style="{
                          backgroundColor:
                            properties.palette.agent.mute_icon_color,
                        }"
                      ></div>
                    </div>
                  </div>
                </div>
              </div> -->
            </div>
          </div>
        </div>

        <!-- 2. Settings Panel -->
        <div class="editor-settings">
          <h3 class="settings-title">
            {{ tabs.find((t) => t.id === activeTab)?.label }} Settings
          </h3>

          <div class="settings-content">
            <!-- General / Assets -->
            <div v-if="activeTab === 'general'" class="form-section">
              <DInput :value="name" @input="(e) => { name = e.target.value; }" label="Theme Name" width="100%" class="mb-3" />
              <DInput :value="properties.assets.background_url" @input="(e) => { properties.assets.background_url = e.target.value; }" label="Background URL" width="100%" class="mb-3" />
              <!-- <div class="form-group mb-3">
                <label class="form-label">Logo Full URL</label>
                <input
                  v-model="properties.assets.logo_full_url"
                  type="text"
                  class="form-control"
                />
              </div> -->
              <DInput :value="properties.assets.logo_icon_url" @input="(e) => { properties.assets.logo_icon_url = e.target.value; }" label="Logo Icon URL" width="100%" class="mb-3" />
              <DInput :value="properties.assets.chat_bot_url" @input="(e) => { properties.assets.chat_bot_url = e.target.value; }" label="Chat Agent Avatar" width="100%" class="mb-3" />
              <DInput :value="properties.assets.chat_user_url" @input="(e) => { properties.assets.chat_user_url = e.target.value; }" label="Chat User Avatar" width="100%" class="mb-3" />
              <DInput :value="properties.assets.splash_bg_url" @input="(e) => { properties.assets.splash_bg_url = e.target.value; }" label="Splash Background URL" width="100%" class="mb-3" />
            </div>

            <!-- Layout -->
            <div v-if="activeTab === 'layout'" class="form-section">
              <!-- <div class="form-group mb-3">
                <label class="form-label">Border Radius (px)</label>
                <input
                  v-model="properties.layout.border_radius"
                  type="number"
                  class="form-control"
                />
              </div>
              <div class="form-group mb-3">
                <label class="form-label">Card Padding (px)</label>
                <input
                  v-model="properties.layout.card_padding"
                  type="number"
                  class="form-control"
                />
              </div> -->
              <DInput :value="properties.layout.header_bottom_radius != null ? String(properties.layout.header_bottom_radius) : ''" @input="(e) => { properties.layout.header_bottom_radius = e.target.value ? Number(e.target.value) : null; }" label="Header Bottom Radius (px)" width="100%" type="number" class="mb-3" />
            </div>

            <!-- Palette -->
            <div v-if="activeTab === 'palette'" class="form-section">
              <Accordion
                :multiple="false"
                v-model:activeIndex="activePaletteIndex"
              >
                <AccordionTab header="Main Colors">
                  <div class="color-grid">
                    <div class="color-input">
                      <label>Brand Primary</label>
                      <input
                        v-model="properties.palette.brand_primary"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>Main Background</label>
                      <input
                        v-model="properties.palette.main_background_color"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>Card Background</label>
                      <input
                        v-model="properties.palette.card_background"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>Card Border</label>
                      <input
                        v-model="properties.palette.card_border_color"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>Text Primary</label>
                      <input
                        v-model="properties.palette.text_primary"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>Text Secondary</label>
                      <input
                        v-model="properties.palette.text_secondary"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                  </div>
                </AccordionTab>

                <!-- <h6 class="subsection-title mt-4">Typography Colors</h6>
                <div class="color-grid">

                </div> -->

                <AccordionTab header="Header">
                  <div class="color-grid">
                    <div class="color-input">
                      <label>Header Color</label>
                      <input
                        v-model="properties.palette.header.header_color"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>Header Logo Background</label>
                      <input
                        v-model="
                          properties.palette.header.header_logo_background
                        "
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                  </div>
                </AccordionTab>

                <AccordionTab header="Chat">
                  <div class="color-grid">
                    <div class="color-input">
                      <label>Chat Background</label>
                      <input
                        v-model="properties.palette.chat.chat_background"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>Chat Title Background</label>
                      <input
                        v-model="properties.palette.chat.chat_title_background"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>Chat Title Color</label>
                      <input
                        v-model="properties.palette.chat.chat_title_color"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>Bot Bubble</label>
                      <input
                        v-model="properties.palette.chat.chat_bubble_bot"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>Bot Text Color</label>
                      <input
                        v-model="properties.palette.chat.text_color_bot"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>User Bubble</label>
                      <input
                        v-model="properties.palette.chat.chat_bubble_user"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>User Text Color</label>
                      <input
                        v-model="properties.palette.chat.text_color_user"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                  </div>
                </AccordionTab>

                <AccordionTab header="Agent Palette">
                  <div class="color-grid">
                    <div class="color-input">
                      <label>Start Session Background</label>
                      <input
                        v-model="
                          properties.palette.agent.start_session_background_btn
                        "
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>Start Session Text</label>
                      <input
                        v-model="
                          properties.palette.agent.start_session_text_btn
                        "
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>End Session Background</label>
                      <input
                        v-model="
                          properties.palette.agent.end_session_background_btn
                        "
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>End Session Text</label>
                      <input
                        v-model="properties.palette.agent.end_session_text_btn"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>Chat Note Text</label>
                      <input
                        v-model="properties.palette.agent.chat_note_text_color"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>Refresh Button Background</label>
                      <input
                        v-model="
                          properties.palette.agent.refresh_background_btn
                        "
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>Refresh Icon Color</label>
                      <input
                        v-model="properties.palette.agent.refresh_icon_color"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>Mute Button Background</label>
                      <input
                        v-model="properties.palette.agent.mute_background_btn"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>Mute Button Icon</label>
                      <input
                        v-model="properties.palette.agent.mute_icon_color"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>Send Button Color</label>
                      <input
                        v-model="properties.palette.agent.send_btn_color"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>Record Button Background</label>
                      <input
                        v-model="properties.palette.agent.record_background_btn"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>Record Icon Color</label>
                      <input
                        v-model="properties.palette.agent.record_icon_color"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                  </div>
                </AccordionTab>

                <AccordionTab header="Product Palette">
                  <div class="color-grid">
                    <div class="color-input">
                      <label>Product Title</label>
                      <input
                        v-model="properties.palette.product.product_title"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>Product Description</label>
                      <input
                        v-model="properties.palette.product.product_description"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                  </div>
                </AccordionTab>

                <AccordionTab header="Menu Cart Palette">
                  <div class="color-grid">
                    <div class="color-input">
                      <label>Menu Cart Background</label>
                      <input
                        v-model="properties.palette.menu.menu_background"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>Menu Title Color</label>
                      <input
                        v-model="properties.palette.menu.menu_title_color"
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                    <div class="color-input">
                      <label>Menu Plus Button Background Color</label>
                      <input
                        v-model="
                          properties.palette.menu.menu_plus_btn_background_color
                        "
                        type="color"
                        class="form-control p-1"
                      />
                    </div>
                  </div>
                </AccordionTab>
              </Accordion>
            </div>

            <!-- Typography -->
            <div v-if="activeTab === 'typography'" class="form-section">
              <DInput :value="properties.typography.font_family_url" @input="(e) => { properties.typography.font_family_url = e.target.value; }" label="Font Family URL" width="100%" class="mb-3" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </AgentLayout>
</template>
<style scoped lang="scss">
.theme-editor-wrapper {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 60px); /* Adjust based on navbar height */
  background: #f8f9fa;
  overflow: hidden;
  border-radius: 10px;
}

.editor-header {
  height: 60px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.5rem;
  flex-shrink: 0;
}

.editor-container {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* Sidebar */
.editor-sidebar {
  width: 240px;
  background: white;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
}

.sidebar-title {
  padding: 1rem 1.5rem;
  font-weight: 600;
  color: #4b5563;
  font-size: 0.875rem;
  text-transform: uppercase;
  border-bottom: 1px solid #f3f4f6;
}

.nav-menu {
  padding: 1rem 0;
}

.nav-item {
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0.75rem 1.5rem;
  gap: 0.75rem;
  background: none;
  border: none;
  color: #6b7280;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  border-radius: 0px !important;

  &:hover {
    background: #f9fafb;
    color: #111827;
  }

  &.active {
    background: #f2f3f7;
    color: var(--primary-color);
  }

  i {
    font-size: 1.1rem;
  }
}

/* Settings Panel */
.editor-settings {
  width: 395px;
  background: white;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.settings-title {
  padding: 1.5rem 1.75rem;
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  border-bottom: 1px solid #f3f4f6;
}

.settings-content {
  padding: 1.5rem 1.75rem 2rem;
}

.editor-settings {
  :deep(.p-inputtext.p-component:not(.p-dropdown-label):not(.p-multiselect-label):not(.p-multiselect-filter):not(.p-dropdown-filter):not(.p-inputnumber-input)),
  :deep(.p-multiselect),
  :deep(.p-dropdown) {
    width: 100% !important;
    max-width: 100% !important;
  }
}

.form-label {
  font-size: 0.85rem;
  color: #374151;
  font-weight: 500;
}
.form-control {
  font-size: 0.9rem;
  border-radius: 6px;
}
.subsection-title {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #1e293b;
  font-weight: 700;
  margin-bottom: 1rem;
  margin-top: 1rem;
}

.color-grid {
  display: grid;
  grid-template-columns: 3fr 3fr;
  gap: 1rem;
}

.color-input {
  label {
    display: block;
    font-size: 0.75rem;
    margin-bottom: 0.25rem;
    color: #6b7280;
  }
  input[type="color"] {
    width: 100%;
    height: 36px;
    padding: 2px;
    border-radius: 6px;
    border: 1px solid #d1d5db;
  }
}

/* Preview Area */
.editor-preview {
  flex: 1;
  background: #f3f4f6;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.preview-header {
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  color: #6b7280;
  text-align: right;
}

.preview-stage {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  overflow: auto;
  padding-top: 100px !important;
}

/* Simulated Mobile Interface */
.mobile-device {
  width: 430px;
  height: 750px; /* iPhone SE-ish */
  background: var(--main-bg);
  background-size: cover;
  background-position: center;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  font-family: var(--font-family);
  border: 8px solid #1e293b;
}

/* New Header Design */
.header-container {
  background: var(--brand-primary); /* Uses main brand color */
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
  padding: 10px 15px;
  color: white;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.status-bar-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo-placeholder {
  width: 35px;
  height: 35px;
  background: var(--header-logo-bg); /* Dark blue circle from screenshot */
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6rem;
  font-weight: bold;
  color: white;
}

.brand-logo img {
  height: 35px;
  width: 35px;
  // border-radius: 50%;
  background: var(--header-logo-bg);
  // padding: 2px;
}

.status-right {
  display: flex;
  align-items: center;
  gap: 15px;
  font-size: 0.85rem;
  color: var(--text-secondary);
  i {
    font-size: 1.1rem;
  }
}

.tabs-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding-top: 5px;
}

.tabs-group {
  display: flex;
  gap: 10px;
}

.header-tab {
  padding: 6px 20px;
  font-size: 0.85rem;
  color: white;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px 8px 8px 0; /* Angled shape approximation */
  // padding-right: 25px;
  cursor: pointer;
  text-transform: capitalize;

  &.active {
    // background: rgba(255, 255, 255, 0.4);
    font-weight: 500;
  }
}

.cart-icon {
  color: var(
    --brand-primary
  ); /* This will effectively make it invisible against same bg unless we change it. */
  color: #ff5722; /* Fallback or specific color */
  font-size: 1.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.9); /* White circle for cart? */
  /* Screenshot shows cart icon is Orange on Grey background?
       Actually the screenshot has the header ending, and content bg is grey.
       The cart is OUTSIDE the orange header in the screenshot?
       Looking at screenshot:
       Orange Header (Status + Time) -> rounded bottom.
       Below that: Grey background row with Tabs (Orange) + Cart (Orange).
    */
  /* Correction based on closer look at screenshot */
}

/* RE-ADJUSTING BASED ON SCREENSHOT ANALYSIS */
/* The screenshot shows:
   1. Orange Top Bar with Logo (Left) and Wifi/Time (Right). Full width, rounded bottom corners.
   2. Below that, a Grey background area (`app-content` or sub-header).
   3. In that Grey area: Two Orange Tabs (Home, Category) on the left.
   4. A Cart icon on the Right side of that same Grey area.
*/

/* Resetting styles to match this specific layout */
.header-container {
  /* Only the top orange bar */
  background: var(--header-bg);
  border-bottom-left-radius: var(--header-border-bottom-left-radius);
  border-bottom-right-radius: var(--header-border-bottom-right-radius);
  padding: 8px 15px;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 50px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  z-index: 10;
  position: relative;
}

.status-bar-row {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Sub-header area for Tabs and Cart */
.sub-header-row {
  // background: #e5e5e5; /* Grey background from screenshot */
  padding: 10px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.tabs-group {
  display: flex;
  gap: 5px;
}

.header-tab {
  background: var(--brand-primary);
  color: var(--text-secondary);
  padding: 6px 20px 6px 15px;
  font-size: 0.8rem;
  border-radius: 4px;
}

.cart-icon {
  color: var(--brand-primary);
  font-size: 1.5rem;
  background: none;
}

.app-content {
  flex: 1;
  padding: 1rem;
  position: relative;
}

.product-card {
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: var(--card-padding);
  width: 160px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.product-image-placeholder {
  width: 100%;
  height: 100px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
  border-radius: 4px;
  i {
    font-size: 2rem;
    color: #cbd5e1;
  }
}

.product-title {
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0;
  margin: 0;
  color: var(--product-title);
}
.product-desc {
  font-size: 0.75rem;
  margin: 0.25rem 0 0.5rem;
  color: var(--product-desc);
}

.price-btn {
  width: 100%;
  background: var(--brand-primary);
  color: var(--text-secondary);
  border: none;
  padding: 0.25rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

.chat-overlay {
  margin-top: auto;
}

.chat-header-small {
  font-size: 0.8rem;
  color: var(--chat-title-color); /* or dark text if bg is light */
  background: var(--chat-title-bg);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: space-between;
  .status-text {
    color: #10b981;
    font-size: 0.7rem;
  }
}

.chat-messages {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  background: var(--chat-bg);
}

.msg {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  max-width: 90%;
  font-size: 0.85rem;

  &.user-msg {
    align-self: flex-end;
    flex-direction: row;

    span {
      background: var(--user-bubble);
      color: var(--user-text);
      padding: 0.5rem 0.75rem;
      border-radius: 12px 12px 0 12px;
    }
  }

  &.bot-msg {
    align-self: flex-start;

    span {
      background: var(--bot-bubble);
      color: var(--bot-text);
      padding: 0.5rem 0.75rem;
      border-radius: 12px 12px 12px 0;
    }
  }

  .avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  .user-avatar {
    background: var(--user-avatar);
  }

  .bot-avatar {
    background: var(--bot-avatar);
  }
}

/* Cart Overlay Styles */
.cart-overlay {
  position: absolute;
  top: 0;
  right: 0;
  width: 50%;
  height: 100%;
  background: var(--menu-background);
  z-index: 50;
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease-out;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

.cart-header {
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f3f4f6;
}

.cart-title {
  font-size: 1.1rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--menu-title-color);
  i {
    font-size: 1.2rem;
  }
}

.close-btn {
  color: #9ca3af;
  cursor: pointer;
  font-size: 1.1rem;
  &:hover {
    color: #4b5563;
  }
}

.cart-body {
  padding: 10px;
  flex: 1;
  overflow-y: auto;
}

.cart-total-section {
  margin-bottom: 1rem;
  font-weight: 700;
  font-size: 0.95rem;
  color: #111827;
}
.total-price {
  color: var(--brand-primary);
  margin-left: 0.5rem;
}

.checkout-btn {
  width: 100%;
  background: var(--brand-primary);
  color: white;
  border: none;
  padding: 0.75rem;
  border-radius: 8px;
  font-weight: 600;
  margin-bottom: 2rem;
  font-size: 12px;
}

/* Cart Item Layout */
.cart-item {
  border: none;
  border-radius: 12px;
  padding: 0.75rem;
  background: var(--card-bg);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.cart-item-top {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.item-thumb-placeholder {
  width: 60px;
  height: 60px;
  background: #d1d5db;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  i {
    color: white;
    font-size: 1.5rem;
  }
}

.item-name {
  font-weight: 700;
  font-size: 0.95rem;
  color: var(--primary-brand);
  flex: 1; /* Pushes remove button to right if needed, or use margin-auto */
  margin-top: 5px; /* Visual alignment with top of image */
}

.item-remove {
  background: none !important;
  border: none;
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  color: #ef4444;
  cursor: pointer;
  flex-shrink: 0;
  margin-left: auto; /* Force to far right */

  &:hover {
    background: #fee2e2;
  }
}

.cart-item-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Old classes removed: item-header-row, item-controls-row */

.item-price {
  font-size: 12px;
  font-weight: 600;
  color: var(--brand-primary);
}

.quantity-controls {
  display: flex;
  align-items: center;
  gap: 2px; /* Gap for separation */
}

.qty-btn {
  border: none;
  background: var(--menu-plus-btn-background-color); /* Very light grey */
  width: 25px;
  height: 25px;
  font-size: 12px;
  color: var(--text-primary);
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px; /* Slight rounding if needed, or keeping square-ish as per screenshot */
}

.qty-val {
  background: var(--menu-plus-btn-background-color);
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
  border-radius: 4px;
}

.app-footer {
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px;
  background: var(--chat-bg);
  margin-top: 10px;
}

.control-group {
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &.chat-note-text {
    color: var(--chat-note-text-color);
  }

  &.center {
    flex-direction: row; /* Changed from column to row */
    gap: 1rem;
    span {
      font-size: 0.9rem;
      font-weight: 500;
      color: #111827;
    }
  }
}

.circle-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: none;
  background: #ef4444; /* Default Red */
  background: var(--btn-refresh-bg);
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;

  &.volume {
    background: white; /* Default White */
    background: var(--btn-mute-bg);
    color: #111827;
  }
}

.record-btn {
  width: 45px;
  height: 45px;
  background: var(--btn-record-bg);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  .stop-square {
    width: 20px;
    height: 20px;
    background: var(--btn-record-icon-color);
    // border-radius: 4px;
  }

  .mic-icon {
    font-size: 20px;
    color: var(--btn-record-icon-color);
  }
}

.end-session-btn {
  background: var(--btn-end-bg);
  color: var(--btn-end-text);
  border: none;
  padding: 0.6rem 1.25rem;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.form-group input {
  width: -webkit-fill-available;
}

.avatar-img {
  width: 24px;
  height: 24px;
  border-radius: 50%;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.volume-up-section {
  margin-top: -25px;
}

.volume-up-icon {
  font-size: 30px;
}

/* ── Dark mode ─────────────────────────────────────────────── */
[data-theme="dark"] .theme-editor-wrapper {
  background: #0d1117;
}

[data-theme="dark"] .editor-header {
  background: #161c2c;
  border-bottom-color: #2d3f5a;
  color: #e8edf5;
}

[data-theme="dark"] .header-center .text-gray-500 {
  color: #9aadc4 !important;
}

[data-theme="dark"] .header-center strong {
  color: #e8edf5;
}

[data-theme="dark"] .editor-sidebar {
  background: #161c2c;
  border-right-color: #2d3f5a;
}

[data-theme="dark"] .sidebar-title {
  color: #9aadc4;
  border-bottom-color: #2d3f5a;
}

[data-theme="dark"] .nav-item {
  color: #9aadc4;

  &:hover {
    background: #1e2a3e;
    color: #e8edf5;
  }

  &.active {
    background: #1a2d52;
    color: #a8c4ff;
  }
}

[data-theme="dark"] .editor-settings {
  background: #161c2c;
  border-right-color: #2d3f5a;
}

[data-theme="dark"] .settings-title {
  color: #e8edf5;
  border-bottom-color: #2d3f5a;
}

[data-theme="dark"] .form-label {
  color: #9aadc4;
}

[data-theme="dark"] .subsection-title {
  color: #e8edf5;
}

[data-theme="dark"] .color-input label {
  color: #9aadc4;
}

[data-theme="dark"] .color-input input[type="color"] {
  border-color: #3d5070;
  background: #1e2a3e;
}

[data-theme="dark"] .editor-preview {
  background: #0d1117;
}

[data-theme="dark"] .preview-header {
  color: #9aadc4;
}

/* ── Skeleton loader (mirrors theme-editor-wrapper layout) ──── */
.theme-builder-skeleton {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: #f3f4f6;
  --tb-skel-base: #e7ebf2;
  --tb-skel-shine: #f4f6fa;
  --tb-skel-surface: #ffffff;
  --tb-skel-border: #e5e7eb;
  --tb-skel-soft-border: #f3f4f6;
  --tb-skel-preview-bg: #f3f4f6;
  --tb-skel-device-frame: #1e293b;
  --tb-skel-device-bg: #ffffff;
}

/* Header — matches .editor-header (white bar w/ bottom border) */
.tb-skel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 16px 24px;
  background: var(--tb-skel-surface);
  border-bottom: 1px solid var(--tb-skel-border);
  flex-shrink: 0;
}

.tb-skel-header__left {
  flex: 1;
}

.tb-skel-header__center {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tb-skel-header__right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  justify-content: flex-end;
}

/* Container — flex row, matches .editor-container */
.tb-skel-container {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* Sidebar — 240px white, matches .editor-sidebar */
.tb-skel-sidebar {
  width: 240px;
  background: var(--tb-skel-surface);
  border-right: 1px solid var(--tb-skel-border);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.tb-skel-sidebar__title {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--tb-skel-soft-border);
}

.tb-skel-nav {
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
}

.tb-skel-nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;

  &--active {
    background: #f2f3f7;
  }
}

/* Settings — 395px white, matches .editor-settings */
.tb-skel-settings {
  width: 395px;
  background: var(--tb-skel-surface);
  border-left: 1px solid var(--tb-skel-border);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.tb-skel-settings__title {
  padding: 1.5rem 1.75rem;
  border-bottom: 1px solid var(--tb-skel-soft-border);
}

.tb-skel-settings__content {
  padding: 1.5rem 1.75rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow-y: auto;
}

/* DInput-shaped field placeholders (55px height with floating label) */
.tb-skel-input-field {
  position: relative;
  height: 55px;
  border-radius: 8px;
  background: var(--tb-skel-base);
  overflow: hidden;
}

.tb-skel-input-field::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    var(--tb-skel-shine) 50%,
    transparent 100%
  );
  transform: translateX(-100%);
  animation: tb-skel-shimmer 1.4s ease-in-out infinite;
}

.tb-skel-input-field__label {
  position: absolute;
  top: 10px;
  left: 14px;
  width: 90px;
  height: 9px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.55);
  z-index: 1;
}

.tb-skel-input-field__input {
  position: absolute;
  bottom: 12px;
  left: 14px;
  width: 60%;
  height: 12px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.45);
  z-index: 1;
}

/* Preview — gray bg, matches .editor-preview */
.tb-skel-preview {
  flex: 1;
  background: var(--tb-skel-preview-bg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tb-skel-preview__header {
  padding: 0.75rem 1.5rem;
  display: flex;
  justify-content: flex-end;
}

.tb-skel-preview__stage {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  overflow: auto;
}

/* Device — matches .mobile-device proportions (430×750 scaled to fit) */
.tb-skel-device {
  width: 360px;
  height: 640px;
  background: var(--tb-skel-device-bg);
  border-radius: 20px;
  border: 8px solid var(--tb-skel-device-frame);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* Header container (brand area at top) */
.tb-skel-device__header {
  background: linear-gradient(135deg, #c7d2fe 0%, #a5b4fc 100%);
  padding: 12px 16px;
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
}

.tb-skel-device__statusbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.tb-skel-device__status-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Sub-header (tabs + cart) */
.tb-skel-device__subheader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f1f5f9;
}

.tb-skel-device__tabs {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Product card */
.tb-skel-device__content {
  flex: 1;
  padding: 20px 16px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.tb-skel-product {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 16px;
  border-radius: 14px;
  background: #fafafa;
  border: 1px solid var(--tb-skel-soft-border);
}

/* Chat overlay */
.tb-skel-device__chat {
  background: #ffffff;
  border-top: 1px solid var(--tb-skel-soft-border);
  padding: 10px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tb-skel-chat__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 6px;
}

.tb-skel-chat__messages {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tb-skel-msg {
  display: flex;
  align-items: center;
  gap: 8px;

  &--user {
    justify-content: flex-end;
  }

  &--bot {
    justify-content: flex-start;
  }
}

/* ── Shimmer primitives ────────────────────────────────────── */
.tb-skel-line,
.tb-skel-circle,
.tb-skel-button,
.tb-skel-block,
.tb-skel-pill,
.tb-skel-bubble {
  position: relative;
  overflow: hidden;
  background: var(--tb-skel-base);
  border-radius: 6px;
  flex-shrink: 0;
}

.tb-skel-line::after,
.tb-skel-circle::after,
.tb-skel-button::after,
.tb-skel-block::after,
.tb-skel-pill::after,
.tb-skel-bubble::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    var(--tb-skel-shine) 50%,
    transparent 100%
  );
  transform: translateX(-100%);
  animation: tb-skel-shimmer 1.4s ease-in-out infinite;
}

@keyframes tb-skel-shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

/* Line variants */
.tb-skel-line {
  height: 12px;
  width: 100%;
  border-radius: 4px;
}

.tb-skel-line--breadcrumb { width: 220px; height: 12px; }
.tb-skel-line--label-xs   { width: 50px;  height: 10px; }
.tb-skel-line--label-md   { width: 110px; height: 12px; }
.tb-skel-line--section    { width: 110px; height: 11px; }
.tb-skel-line--nav        { width: 100px; height: 11px; }
.tb-skel-line--heading    { width: 160px; height: 16px; }
.tb-skel-line--preview-label { width: 90px; height: 10px; }
.tb-skel-line--clock      { width: 70px; height: 8px; background: rgba(255, 255, 255, 0.6); }
.tb-skel-line--product-title { width: 60px; height: 12px; }
.tb-skel-line--product-desc  { width: 110px; height: 9px; }
.tb-skel-line--chat-title    { width: 90px;  height: 11px; }
.tb-skel-line--chat-status   { width: 50px;  height: 9px; }

/* Circles */
.tb-skel-circle { border-radius: 50%; }
.tb-skel-circle--icon  { width: 18px; height: 18px; }
.tb-skel-circle--logo  { width: 35px; height: 35px; background: rgba(255, 255, 255, 0.6); }
.tb-skel-circle--xxs   { width: 12px; height: 12px; background: rgba(255, 255, 255, 0.5); }
.tb-skel-circle--xs    { width: 22px; height: 22px; }
.tb-skel-circle--avatar{ width: 28px; height: 28px; }

/* Buttons */
.tb-skel-button { height: 32px; border-radius: 8px; }
.tb-skel-button--ghost   { width: 100px; }
.tb-skel-button--primary {
  width: 110px;
  background: linear-gradient(90deg, #4e7fff66, #6b96ff66);
}

/* Pills (tabs / price button) */
.tb-skel-pill { width: 60px; height: 18px; border-radius: 999px; }
.tb-skel-pill--active { background: #cbd5e1; }
.tb-skel-pill--price  { width: 90px; height: 28px; }

/* Block (product image) */
.tb-skel-block--image {
  width: 100%;
  height: 130px;
  border-radius: 10px;
}

/* Chat bubbles */
.tb-skel-bubble { height: 28px; border-radius: 14px; }
.tb-skel-bubble--user { width: 130px; background: #c7d2fe; }
.tb-skel-bubble--bot  { width: 160px; }

/* ── Dark mode ─────────────────────────────────────────────── */
[data-theme="dark"] .theme-builder-skeleton {
  background: #0d1117;
  --tb-skel-base: #1e2a3e;
  --tb-skel-shine: #28364e;
  --tb-skel-surface: #161c2c;
  --tb-skel-border: #2d3f5a;
  --tb-skel-soft-border: #2d3f5a;
  --tb-skel-preview-bg: #0d1117;
  --tb-skel-device-frame: #0a0f1a;
  --tb-skel-device-bg: #161c2c;
}

[data-theme="dark"] .tb-skel-nav-item--active {
  background: rgba(107, 150, 255, 0.1);
}

[data-theme="dark"] .tb-skel-button--primary {
  background: linear-gradient(90deg, #4e7fff80, #6b96ff80);
}

[data-theme="dark"] .tb-skel-input-field__label,
[data-theme="dark"] .tb-skel-input-field__input {
  background: rgba(232, 237, 245, 0.1);
}

[data-theme="dark"] .tb-skel-device__header {
  background: linear-gradient(135deg, #1a2d52 0%, #28364e 100%);
}

[data-theme="dark"] .tb-skel-device__subheader {
  background: #1e2a3e;
}

[data-theme="dark"] .tb-skel-product {
  background: #1a2238;
  border-color: #2d3f5a;
}

[data-theme="dark"] .tb-skel-device__chat {
  background: #161c2c;
  border-top-color: #2d3f5a;
}

[data-theme="dark"] .tb-skel-line--clock,
[data-theme="dark"] .tb-skel-circle--logo,
[data-theme="dark"] .tb-skel-circle--xxs {
  background: rgba(232, 237, 245, 0.18);
}

[data-theme="dark"] .tb-skel-pill--active {
  background: #3d5070;
}

[data-theme="dark"] .tb-skel-bubble--user {
  background: #1a2d52;
}

[data-theme="dark"] .tb-skel-device {
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.55);
}
</style>
